import { api } from '@agency/shared';
import { Chip, Code, DataTable, Dialog, EmptyState, KeyValue, MetricCell, MetricRow, Skeleton, StatusDot, Tabs, Timeline, fmtCount, fmtMicroUsd, fmtTimestamp, type DataTableColumn, type TimelineItem } from '@agency/ui';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ErrorCallout, IdCell, Stamp, UserLink } from '../../app/widgets.js';
import { AI_KEY_STATUS_LABEL, AI_KEY_STATUS_TONE, OUTCOME_TONE, REVOCATION_LABEL, REVOCATION_TONE } from '../../lib/labels.js';
import { useApi } from '../../lib/useApi.js';

const GRANT_TONE: Record<api.CredentialGrant['result'], 'ok' | 'warn' | 'info' | 'brand' | 'mute'> = { issued: 'info', redeemed: 'ok', expired: 'mute', denied: 'brand', revoked: 'mute' };

export function KeyHistoryDrawer({ aiKey, onClose }: { aiKey: api.AiKey | null; onClose: () => void }) {
  const [tab, setTab] = useState<'grants' | 'assignments' | 'usage' | 'events'>('grants');
  const history = useApi('admin.aiKeys.history', { params: { aiKeyId: (aiKey?.id ?? '') as api.AiKey['id'] } }, { enabled: aiKey !== null });
  const usage = useApi('admin.usage.query', { input: { aiKeyId: (aiKey?.id ?? '') as api.AiKey['id'], limit: 50 } }, { enabled: aiKey !== null });
  const h = history.data;
  const counts = useMemo(() => {
    const grants = h?.recentGrants ?? [];
    return {
      issued: grants.filter((g) => g.result === 'issued' || g.result === 'redeemed' || g.result === 'expired').length,
      redeemed: grants.filter((g) => g.result === 'redeemed').length,
      denied: grants.filter((g) => g.result === 'denied').length,
    };
  }, [h]);

  const grantColumns = useMemo<DataTableColumn<api.CredentialGrant>[]>(
    () => [
      { id: 'issued', header: 'Issued', cell: ({ row }) => <Stamp value={row.original.issuedAt} />, size: 150 },
      { id: 'result', header: 'Result', cell: ({ row }) => <Chip tone={GRANT_TONE[row.original.result]}>{row.original.result}</Chip>, size: 90 },
      { id: 'reason', header: 'Reason', cell: ({ row }) => <span className="font-mono text-2xs text-mute">{row.original.denialReason ?? '--'}</span>, size: 150 },
      { id: 'user', header: 'User', cell: ({ row }) => <UserLink userId={row.original.userId} />, size: 130 },
      { id: 'device', header: 'Device', cell: ({ row }) => <IdCell value={row.original.deviceId} />, size: 110 },
      { id: 'operation', header: 'Operation', cell: ({ row }) => <IdCell value={row.original.operationId} />, size: 110 },
      { id: 'budget', header: 'Reservation', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{fmtMicroUsd(row.original.budgetReservationMicroUsd)}</span>, size: 100 },
    ],
    [],
  );
  const assignmentColumns = useMemo<DataTableColumn<api.AiKeyAssignment>[]>(
    () => [
      { id: 'version', header: 'v', cell: ({ row }) => <span className="font-mono tabular-nums">{row.original.version}</span>, size: 40 },
      { id: 'user', header: 'Assigned to', cell: ({ row }) => (row.original.scope === 'organization' ? <span className="text-ink">All users</span> : <UserLink userId={row.original.userId} />), size: 150 },
      { id: 'by', header: 'Assigned by', cell: ({ row }) => <UserLink userId={row.original.assignedByUserId} />, size: 150 },
      { id: 'from', header: 'Valid from', cell: ({ row }) => <Stamp value={row.original.validFrom} />, size: 150 },
      { id: 'to', header: 'Valid to', cell: ({ row }) => <Stamp value={row.original.validTo} />, size: 150 },
      { id: 'end', header: 'End reason', cell: ({ row }) => <span className="font-mono text-2xs text-mute">{row.original.endReason ?? (row.original.validTo ? '--' : 'active')}</span>, size: 110 },
    ],
    [],
  );
  const usageColumns = useMemo<DataTableColumn<api.UsageRecord>[]>(
    () => [
      { id: 'at', header: 'Occurred', cell: ({ row }) => <Stamp value={row.original.occurredAt} />, size: 150 },
      { id: 'model', header: 'Model', cell: ({ row }) => <span className="font-mono text-xs-plus">{row.original.model ?? '--'}</span>, size: 120 },
      { id: 'tokens', header: 'In / out', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{fmtCount(row.original.inputTokens)} / {fmtCount(row.original.outputTokens)}</span>, size: 130 },
      { id: 'cost', header: 'Cost', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.verificationStatus === 'unknown' ? '--' : fmtMicroUsd(row.original.actualCostMicroUsd ?? row.original.estimatedCostMicroUsd)}</span>, size: 90 },
      { id: 'status', header: 'Verification', cell: ({ row }) => <Chip tone={row.original.verificationStatus === 'reconciled' ? 'ok' : row.original.verificationStatus === 'estimated' ? 'warn' : 'mute'}>{row.original.verificationStatus}</Chip>, size: 110 },
    ],
    [],
  );
  const events = useMemo<TimelineItem[]>(
    () =>
      (h?.events ?? []).map((e) => ({
        id: e.eventId,
        meta: [fmtTimestamp(e.occurredAt), `evt ${e.eventId.slice(0, 8)}`],
        delta: e.outcome,
        deltaTone: e.outcome === 'success' ? 'ok' : 'brand',
        nodeTone: e.outcome === 'success' ? 'ok' : 'brand',
        title: <Link to="/audit/$eventId" params={{ eventId: e.eventId }} className="hover:underline">{e.action}</Link>,
        summary: <span className="flex items-center gap-2"><UserLink userId={e.actorUserId} /><Chip tone={OUTCOME_TONE[e.outcome]}>{e.outcome}</Chip></span>,
      })),
    [h],
  );

  return (
    <Dialog open={aiKey !== null} onOpenChange={(o) => !o && onClose()} title={aiKey ? aiKey.label : 'Key'} subtitle={aiKey ? `${AI_KEY_STATUS_LABEL[aiKey.status]} · secret version ${aiKey.secretVersion}` : undefined} width={920}>
      {!aiKey ? null : (
        <div className="flex flex-col gap-4">
          <KeyValue
            layout="grid"
            columns={4}
            items={[
              { key: 'Provider key id', value: <Code>{aiKey.providerKeyId}</Code> },
              { key: 'Identifier', value: <Code>{aiKey.maskedIdentifier}</Code> },
              { key: 'Status', value: <Chip tone={AI_KEY_STATUS_TONE[aiKey.status]}>{AI_KEY_STATUS_LABEL[aiKey.status]}</Chip> },
              { key: 'Owner', value: aiKey.assignmentScope === 'organization' ? 'All users (shared key)' : aiKey.assignedUserId ? <UserLink userId={aiKey.assignedUserId} /> : 'unassigned' },
              { key: 'Provider revocation', value: <StatusDot tone={REVOCATION_TONE[aiKey.providerRevocationState]} label={REVOCATION_LABEL[aiKey.providerRevocationState]} /> },
              { key: 'Base URL', value: aiKey.baseUrl, mono: true },
              { key: 'Rotated from', value: <IdCell value={aiKey.rotatedFromKeyId} /> },
              { key: 'Rotated to', value: <IdCell value={aiKey.rotatedToKeyId} /> },
              { key: 'Revocation error', value: aiKey.providerRevocationError ?? '--', mono: true },
            ]}
          />
          <ErrorCallout error={history.error} onRetry={() => void history.reload()} />
          {history.loading ? (
            <Skeleton height={60} />
          ) : (
            <MetricRow columns={3}>
              <MetricCell label="Issued (recent)" value={fmtCount(counts.issued)} />
              <MetricCell label="Redeemed" value={fmtCount(counts.redeemed)} tone="ok" />
              <MetricCell label="Denied" value={fmtCount(counts.denied)} tone={counts.denied > 0 ? 'brand' : 'ink'} />
            </MetricRow>
          )}
          <Tabs compact aria-label="Key history" value={tab} onValueChange={(v) => setTab(v as typeof tab)} items={[{ value: 'grants', label: 'Grants', count: h?.recentGrants.length ?? 0 }, { value: 'assignments', label: 'Assignments', count: h?.assignments.length ?? 0 }, { value: 'usage', label: 'Usage by key', count: usage.data?.items.length ?? 0 }, { value: 'events', label: 'Events', count: h?.events.length ?? 0 }]} />
          {tab === 'grants' ? (
            <DataTable<api.CredentialGrant> aria-label="Recent grants" columns={grantColumns} rows={h?.recentGrants ?? []} rowKey={(r) => r.id} height={300} state={history.loading ? 'loading' : (h?.recentGrants.length ?? 0) === 0 ? 'empty' : 'complete'} emptyMessage="No credential grants issued for this key." noun="grants" />
          ) : tab === 'assignments' ? (
            <DataTable<api.AiKeyAssignment> aria-label="Assignment ledger" columns={assignmentColumns} rows={h?.assignments ?? []} rowKey={(r) => r.id} height={300} state={history.loading ? 'loading' : (h?.assignments.length ?? 0) === 0 ? 'empty' : 'complete'} emptyMessage="This key has never been assigned." noun="assignments" />
          ) : tab === 'usage' ? (
            <DataTable<api.UsageRecord> aria-label="Usage by key" columns={usageColumns} rows={usage.data?.items ?? []} rowKey={(r) => r.id} height={300} state={usage.loading ? 'loading' : (usage.data?.items.length ?? 0) === 0 ? 'empty' : 'complete'} emptyMessage="No usage reported for this key." noun="records" />
          ) : events.length === 0 ? (
            <EmptyState compact message="No audit events for this key." />
          ) : (
            <div className="scrollbar-thin max-h-[300px] overflow-y-auto"><Timeline items={events} /></div>
          )}
        </div>
      )}
    </Dialog>
  );
}
